/* eslint-disable consistent-return,max-len */
import React from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import 'moment/locale/es';
// @material-ui/icons
import Check from '@material-ui/icons/Check';
import ArrowUp from '@material-ui/icons/ArrowUpward';
// @material-ui/core components
import FilterStyles from 'assets/jss/AdsFilter';
import Checkbox from '@material-ui/core/Checkbox/Checkbox';
import withStyles from '@material-ui/core/styles/withStyles';
import FormControlLabel from '@material-ui/core/FormControlLabel';
// core components
import GridItem from 'components/UI/Grid/GridItem';
import Button from 'components/UI/CustomButtons/Button';
import GridContainer from 'components/UI/Grid/GridContainer';
import CustomInput from 'components/UI/CustomInput/CustomInput';
// our components
import { promotedAdPackagesStatus, locationScopes } from 'config';
import SectionDivider from 'components/common/SectionDivider/SectionDivider';
import CategorySelect from 'components/common/CategorySelect';
import LocationSelect from 'components/common/LocationSelect';
import DurationSelect from 'components/common/DurationSelect';

/**
 * Variable to define default selected status
 */
const defaultSelectedStatus = [promotedAdPackagesStatus.ENABLED];

/**
 * Variable to define default selected location scope
 */
const defaultSelectedLocationScope = [
  locationScopes.NATIONAL,
  locationScopes.PROVINCIAL,
  locationScopes.MUNICIPAL,
];

class PromotedAdPackagesFilter extends React.Component {
  constructor(props) {
    super(props);
    const { appliedFilter } = props;
    this.state = {
      selectedPaidAdsStatus:
        (appliedFilter && appliedFilter.status) || defaultSelectedStatus,
      selectedPaidAdsLocationScope:
        (appliedFilter && appliedFilter.locationScope) ||
        defaultSelectedLocationScope,
      localFilter: appliedFilter || null,
    };
  }

  /**
   * @description
   * Change filters.
   *
   * @param {Object} changedFilter - Changed filters values.
   * @param {*} callback - Callback waiting for setState.
   */
  handleChangeFilter = (changedFilter, callback = null) => {
    const { localFilter } = this.state;
    this.setState(
      { localFilter: { ...localFilter, ...changedFilter } },
      callback,
    );
  };

  /**
   * @description
   * Update selected status.
   *
   * @param {*} callback - Callback waiting for setState.
   */
  updateSelectedCheckboxes = callback => {
    const { selectedPaidAdsStatus } = this.state;
    this.handleChangeFilter(
      selectedPaidAdsStatus.length === 0
        ? { status: undefined }
        : { status: selectedPaidAdsStatus },
      callback,
    );
  };

  /**
   * @description
   * Handle select status checkboxes.
   *
   * @param {string} checkLabel - Status selected.
   * @param {*} callback - Callback waiting for setState.
   * @returns {undefined} No return.
   */
  handleStatusCheck = (checkLabel, callback = null) => {
    const { selectedPaidAdsStatus } = this.state;
    if (selectedPaidAdsStatus.indexOf(checkLabel) !== -1) {
      const newSelected = selectedPaidAdsStatus.filter(
        status => status !== checkLabel,
      );
      this.setState({ selectedPaidAdsStatus: newSelected }, () =>
        this.updateSelectedCheckboxes(callback),
      );
    } else {
      this.setState(
        {
          selectedPaidAdsStatus: [...selectedPaidAdsStatus, checkLabel],
        },
        () => this.updateSelectedCheckboxes(callback),
      );
    }
  };

  /**
   * @description
   * Update selected location scope.
   *
   * @param {*} callback - Callback waiting for setState.
   */
  updateSelectedLocationScopeCheckboxes = callback => {
    const { selectedPaidAdsLocationScope } = this.state;
    this.handleChangeFilter(
      selectedPaidAdsLocationScope.length === 0
        ? { locationScope: undefined }
        : { locationScope: selectedPaidAdsLocationScope },
      callback,
    );
  };

  /**
   * @description
   * Handle select location scope checkboxes.
   *
   * @param {string} checkLabel - Location scope selected.
   * @param {*} callback - Callback waiting for setState.
   * @returns {undefined} No return.
   */
  handleLocationScopeCheck = (checkLabel, callback = null) => {
    const { selectedPaidAdsLocationScope } = this.state;
    if (selectedPaidAdsLocationScope.indexOf(checkLabel) !== -1) {
      const newSelected = selectedPaidAdsLocationScope.filter(
        scope => scope !== checkLabel,
      );
      this.setState({ selectedPaidAdsLocationScope: newSelected }, () =>
        this.updateSelectedLocationScopeCheckboxes(callback),
      );
    } else {
      this.setState(
        {
          selectedPaidAdsLocationScope: [
            ...selectedPaidAdsLocationScope,
            checkLabel,
          ],
        },
        () => this.updateSelectedLocationScopeCheckboxes(callback),
      );
    }
  };

  /**
   * @description
   * Call filter callback.
   */
  handleFilter = () => {
    const { filter } = this.props;
    const { localFilter } = this.state;
    filter({ ...localFilter });
  };

  /**
   * @description
   * Render full filter's box.
   * @returns {*} - AdFilter's box.
   */
  render() {
    const {
      classes,
      restartFilters,
      t,
      showFilters,
      categories,
      provinces,
      durations,
    } = this.props;
    const {
      selectedPaidAdsStatus,
      localFilter,
      selectedPaidAdsLocationScope,
    } = this.state;
    return (
      <div className={classes.expandFilters} style={{ marginBottom: 20 }}>
        <GridContainer>
          <GridItem xs={12} md={6}>
            <SectionDivider label={t('promoted_ad_packages.filters.id')}>
              <GridItem xs={12} md={4}>
                <CustomInput
                  data-cy="packageId"
                  id="packageId"
                  formControlProps={{
                    className: `${classes.top} ${classes.search}`,
                  }}
                  inputProps={{
                    placeholder: t('promoted_ad_packages.filters.id'),
                    inputProps: {
                      'aria-label': 'Search',
                      className: classes.searchInput,
                    },
                    onChange: e => {
                      const { value } = e.target;
                      this.handleChangeFilter({
                        id: value || undefined,
                      });
                    },
                    value: localFilter.id !== undefined ? localFilter.id : '',
                    type: 'number',
                  }}
                />
              </GridItem>
            </SectionDivider>
            <SectionDivider label={t('promoted_ad_packages.filters.ad_id')}>
              <GridItem xs={12} md={4}>
                <CustomInput
                  data-cy="adId"
                  id="adId"
                  formControlProps={{
                    className: `${classes.top} ${classes.search}`,
                  }}
                  inputProps={{
                    placeholder: t('promoted_ad_packages.filters.ad_id'),
                    inputProps: {
                      'aria-label': 'Search',
                      className: classes.searchInput,
                    },
                    onChange: e => {
                      const { value } = e.target;
                      this.handleChangeFilter({
                        ad: value || undefined,
                      });
                    },
                    value: localFilter.ad !== undefined ? localFilter.ad : '',
                    type: 'number',
                  }}
                />
              </GridItem>
            </SectionDivider>
            <SectionDivider label={t('ad.filters.category')}>
              <div className={classes.categorySection}>
                <CategorySelect
                  appliedFilter={localFilter}
                  changeFilter={this.handleChangeFilter}
                  categories={categories}
                />
              </div>
            </SectionDivider>
            <SectionDivider label={t('ad.filters.location')}>
              <div className={classes.categorySection}>
                <LocationSelect
                  provinces={provinces}
                  selectedProvince={localFilter.province}
                  selectedMunicipality={localFilter.municipality}
                  changeFilter={({ province, municipality }) => {
                    if (typeof province !== 'undefined') {
                      this.handleChangeFilter({
                        province: province !== 0 ? province : undefined,
                        municipality: undefined,
                      });
                    }
                    if (typeof municipality !== 'undefined') {
                      this.handleChangeFilter({
                        municipality:
                          municipality !== 0 ? municipality : undefined,
                      });
                    }
                  }}
                />
              </div>
            </SectionDivider>
            <SectionDivider
              label={t('promoted_ad_packages.form.fields.duration')}
            >
              <div className={classes.categorySection}>
                <DurationSelect
                  durations={durations}
                  selectedDuration={localFilter.duration}
                  change={this.handleChangeFilter}
                  selectAll
                />
              </div>
            </SectionDivider>
          </GridItem>
          <GridItem xs={12} md={6}>
            <SectionDivider
              label={t('promoted_ad_packages.filters.location_scope.title')}
            >
              <div style={{ padding: 10 }}>
                <GridContainer>
                  <GridItem xs={12} md={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          onClick={() =>
                            this.handleLocationScopeCheck(
                              locationScopes.NATIONAL,
                            )
                          }
                          checked={
                            selectedPaidAdsLocationScope.indexOf(
                              locationScopes.NATIONAL,
                            ) !== -1
                          }
                          checkedIcon={
                            <Check className={classes.checkedIcon} />
                          }
                          icon={<Check className={classes.uncheckedIcon} />}
                          className={classes.checked}
                        />
                      }
                      id="nationalCheckbox"
                      label={t(
                        'promoted_ad_packages.filters.location_scope.national',
                      )}
                      style={{ width: '100%' }}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          onClick={() =>
                            this.handleLocationScopeCheck(
                              locationScopes.PROVINCIAL,
                            )
                          }
                          checked={
                            selectedPaidAdsLocationScope.indexOf(
                              locationScopes.PROVINCIAL,
                            ) !== -1
                          }
                          checkedIcon={
                            <Check className={classes.checkedIcon} />
                          }
                          icon={<Check className={classes.uncheckedIcon} />}
                          className={classes.checked}
                        />
                      }
                      id="provincialCheckbox"
                      label={t(
                        'promoted_ad_packages.filters.location_scope.provincial',
                      )}
                      style={{ width: '100%' }}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          onClick={() => {
                            this.handleLocationScopeCheck(
                              locationScopes.MUNICIPAL,
                            );
                            this.handleChangeFilter({
                              pendingReason: undefined,
                            });
                          }}
                          checked={
                            selectedPaidAdsLocationScope.indexOf(
                              locationScopes.MUNICIPAL,
                            ) !== -1
                          }
                          checkedIcon={
                            <Check className={classes.checkedIcon} />
                          }
                          icon={<Check className={classes.uncheckedIcon} />}
                          className={classes.checked}
                        />
                      }
                      id="municipalCheckbox"
                      label={t(
                        'promoted_ad_packages.filters.location_scope.municipal',
                      )}
                      style={{ width: '100%' }}
                    />
                    <GridContainer justify="flex-start">
                      <GridItem>
                        <Button
                          color="primary"
                          simple
                          className={classes.buttons}
                          onClick={() =>
                            this.setState(
                              {
                                selectedPaidAdsLocationScope: selectedPaidAdsLocationScope.length
                                  ? []
                                  : [
                                      locationScopes.NATIONAL,
                                      locationScopes.PROVINCIAL,
                                      locationScopes.MUNICIPAL,
                                    ],
                              },
                              this.updateSelectedLocationScopeCheckboxes,
                            )
                          }
                          id="unselectAll"
                          key="unselectAll"
                        >
                          {selectedPaidAdsLocationScope.length
                            ? t('ad.filters.status.uncheck')
                            : t('ad.filters.status.check')}
                        </Button>
                      </GridItem>
                    </GridContainer>
                  </GridItem>
                </GridContainer>
              </div>
            </SectionDivider>
            <SectionDivider
              label={t('promoted_ad_packages.filters.section.status')}
            >
              <GridItem>
                <GridContainer>
                  <GridItem xs={12} md={8}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          onClick={() =>
                            this.handleStatusCheck(
                              promotedAdPackagesStatus.ENABLED,
                            )
                          }
                          checked={
                            selectedPaidAdsStatus.indexOf(
                              promotedAdPackagesStatus.ENABLED,
                            ) !== -1
                          }
                          checkedIcon={
                            <Check className={classes.checkedIcon} />
                          }
                          icon={<Check className={classes.uncheckedIcon} />}
                          className={classes.checked}
                        />
                      }
                      id="activeCheckbox"
                      label={t('promoted_ad_packages.filters.status.enabled')}
                      style={{ width: '100%' }}
                    />
                  </GridItem>
                  <GridItem xs={12} md={4}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          onClick={() =>
                            this.handleStatusCheck(
                              promotedAdPackagesStatus.DISABLED,
                            )
                          }
                          checked={
                            selectedPaidAdsStatus.indexOf(
                              promotedAdPackagesStatus.DISABLED,
                            ) !== -1
                          }
                          checkedIcon={
                            <Check className={classes.checkedIcon} />
                          }
                          icon={<Check className={classes.uncheckedIcon} />}
                          className={classes.checked}
                        />
                      }
                      id="completedCheckbox"
                      label={t('promoted_ad_packages.filters.status.disabled')}
                    />
                  </GridItem>
                  <GridItem xs={12}>
                    <GridContainer justify="flex-end">
                      <GridItem>
                        <Button
                          color="primary"
                          simple
                          className={classes.buttons}
                          onClick={() =>
                            this.setState(
                              {
                                selectedPaidAdsStatus:
                                  selectedPaidAdsStatus.length > 0
                                    ? []
                                    : [
                                        promotedAdPackagesStatus.ENABLED,
                                        promotedAdPackagesStatus.DISABLED,
                                      ],
                              },
                              this.updateSelectedCheckboxes,
                            )
                          }
                          id="unselectAll"
                          key="unselectAll"
                        >
                          {selectedPaidAdsStatus.length > 0
                            ? t('ad.filters.status.uncheck')
                            : t('ad.filters.status.check')}
                        </Button>
                      </GridItem>
                    </GridContainer>
                  </GridItem>
                </GridContainer>
              </GridItem>
            </SectionDivider>
          </GridItem>
        </GridContainer>

        <GridContainer>
          <GridItem xs={12} sm={12} md={6}>
            <Button
              color="primary"
              simple
              className={classes.buttons}
              onClick={showFilters}
              id="hideFilters"
              key="hideFilters"
            >
              <ArrowUp />
              {t('ad.filters.shrink')}
            </Button>
          </GridItem>
          <GridItem xs={12} sm={12} md={6}>
            <GridContainer justify="flex-end">
              <GridItem>
                <Button
                  color="danger"
                  simple
                  className={classes.buttons}
                  onClick={() => {
                    this.setState({
                      selectedPaidAdsStatus: defaultSelectedStatus,
                      selectedPaidAdsLocationScope: defaultSelectedLocationScope,
                      localFilter: {},
                    });
                    restartFilters();
                  }}
                  id="restartFilters"
                  key="restartFilters"
                >
                  {t('ad.filters.clean')}
                </Button>
                <Button
                  data-cy="filterAds"
                  color="primary"
                  className={classes.buttons}
                  onClick={() => {
                    this.handleFilter();
                    showFilters();
                  }}
                  id="filterAds"
                  key="filterAds"
                >
                  {t('promoted_ad_packages.filters.filter')}
                </Button>
              </GridItem>
            </GridContainer>
          </GridItem>
        </GridContainer>
      </div>
    );
  }
}

/**
 * PromotedAdPackagesFilter PropTypes
 * @param classes - styles received from withStyles
 * @param appliedFilter - prop to know applied filters
 * @param t - prop from HOC with string translations
 * @param categories - prop know categories and subcategories
 * @param provinces - prop know provinces and municipalities
 * @param durations - prop know durations
 * @callback filter - callback called when user clicks filter button
 * @callback showFilters - callback called when user clicks filters button
 * @callback restartFilters - callback called when user restart filters button
 */

PromotedAdPackagesFilter.propTypes = {
  classes: PropTypes.instanceOf(Object).isRequired,
  appliedFilter: PropTypes.shape({}).isRequired,
  filter: PropTypes.func.isRequired,
  showFilters: PropTypes.func.isRequired,
  restartFilters: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  categories: PropTypes.instanceOf(Array).isRequired,
  provinces: PropTypes.instanceOf(Array).isRequired,
  durations: PropTypes.instanceOf(Array).isRequired,
};

export default withTranslation()(
  withStyles(FilterStyles)(PromotedAdPackagesFilter),
);
